import { businessService } from '../services/api/businessService';

let slugByBusinessIdCache = null;
let slugByBusinessIdLoadPromise = null;

/** Cached BusinessId → BusinessSlug map from published businesses. */
export async function loadSlugByBusinessIdMap() {
  if (slugByBusinessIdCache) {
    return slugByBusinessIdCache;
  }
  if (!slugByBusinessIdLoadPromise) {
    slugByBusinessIdLoadPromise = businessService
      .getAllBusinesses()
      .then((businesses) => {
        const map = {};
        for (const biz of businesses || []) {
          if (biz?.BusinessId && biz?.BusinessSlug) {
            map[biz.BusinessId] = biz.BusinessSlug;
          }
        }
        slugByBusinessIdCache = map;
        return map;
      })
      .catch(() => {
        slugByBusinessIdLoadPromise = null;
        return {};
      });
  }
  return slugByBusinessIdLoadPromise;
}

/**
 * Resolve a storefront slug synchronously when already on the object or in a lookup map.
 *
 * @param {string | { BusinessSlug?: string, BusinessId?: string } | null | undefined} businessOrSlug
 * @param {Record<string, string> | undefined} slugByBusinessId
 */
export function resolveStorefrontSlug(businessOrSlug, slugByBusinessId) {
  if (typeof businessOrSlug === 'string') {
    const trimmed = businessOrSlug.trim();
    return trimmed || '';
  }

  if (!businessOrSlug || typeof businessOrSlug !== 'object') {
    return '';
  }

  const direct =
    typeof businessOrSlug.BusinessSlug === 'string'
      ? businessOrSlug.BusinessSlug.trim()
      : '';
  if (direct) {
    return direct;
  }

  const businessId = businessOrSlug.BusinessId;
  if (businessId && slugByBusinessId?.[businessId]) {
    return String(slugByBusinessId[businessId]).trim();
  }

  return '';
}

/** Resolve slug using BusinessSlug on the object, else getAllBusinesses lookup by BusinessId. */
export async function resolveStorefrontSlugAsync(businessOrSlug) {
  const map = await loadSlugByBusinessIdMap();
  return resolveStorefrontSlug(businessOrSlug, map);
}

/**
 * Build the public storefront path for a business or slug string.
 *
 * @param {string | { BusinessSlug?: string, BusinessId?: string } | null | undefined} businessOrSlug
 * @param {Record<string, string> | undefined} slugByBusinessId
 */
export function getStorefrontPath(businessOrSlug, slugByBusinessId) {
  const slug = resolveStorefrontSlug(businessOrSlug, slugByBusinessId);
  return slug ? `/storefront/${slug}` : '/storefront';
}

export async function getStorefrontPathAsync(businessOrSlug) {
  const slug = await resolveStorefrontSlugAsync(businessOrSlug);
  return slug ? `/storefront/${slug}` : '/storefront';
}

/**
 * Navigate to a storefront: use BusinessSlug when present, otherwise look up slug by BusinessId.
 *
 * @param {import('react-router-dom').NavigateFunction} navigate
 * @param {string | { BusinessSlug?: string, BusinessId?: string } | null | undefined} businessOrSlug
 * @param {import('react-router-dom').NavigateOptions | undefined} options
 */
export async function navigateToStorefront(navigate, businessOrSlug, options) {
  const path = await getStorefrontPathAsync(businessOrSlug);
  navigate(path, options);
}
